/**
 * Session HTTP calls + navigation helpers for the OAuth2-BFF (token-handler) flow.
 *
 * The SPA never holds tokens: `login`/`logout` are full-page navigations to the BFF
 * (which runs the OAuth2 Authorization-Code + PKCE dance with Keycloak), while the
 * session/permissions/tenants are read from `GET /api/v1/me` over the cookie session.
 */
import { apiClient } from '@/shared/api';
import { env } from '@/shared/config/env';

import type { MeResponse } from './sessionTypes';

const BASE = '/api/v1';

/** Bootstrap the current session. 401 ⇒ unauthenticated. */
export const fetchMe = async (): Promise<MeResponse> =>
  (await apiClient.get<MeResponse>(`${BASE}/me`)).data;

/** Switch the active tenant (membership-validated server-side); returns refreshed session. */
export const putActiveTenant = async (tenantId: string): Promise<MeResponse> =>
  (await apiClient.put<MeResponse>(`${BASE}/session/tenant`, { tenantId })).data;

function bffUrl(path: string): string {
  return new URL(path, env.apiBaseUrl).toString();
}

/** Full-page redirect to the BFF authorize endpoint (Keycloak login). */
export function redirectToLogin(): void {
  window.location.assign(bffUrl('/oauth2/authorization/keycloak'));
}

/**
 * Log out via an **XHR** `POST /logout` (not a form navigation): the apiClient attaches the
 * `X-XSRF-TOKEN` header — the same CSRF path every other mutation uses — so logout works whenever
 * those do. The BFF invalidates the cookie session and replies `200 {"logoutUrl"}` with Keycloak's
 * end-session URL; the SPA then performs the top-level navigation (which an XHR could not follow
 * across the Keycloak origin) to terminate the SSO session, after which Keycloak bounces back to the
 * SPA's welcome page.
 *
 * Robustness: any failure (CSRF, expired/absent session, network) is caught and the user is sent to
 * the public welcome page instead of being stranded on a server error page — they are logged out of
 * the SPA regardless.
 */
export function submitLogout(): void {
  apiClient
    .post<{ logoutUrl?: string }>('/logout', null, { headers: { Accept: 'application/json' } })
    .then((response) => navigateAfterLogout(response.data?.logoutUrl))
    .catch(() => navigateAfterLogout(undefined));
}

function navigateAfterLogout(logoutUrl?: string): void {
  window.location.assign(logoutUrl && logoutUrl.length > 0 ? logoutUrl : welcomeUrl());
}

function welcomeUrl(): string {
  return new URL('/welcome', window.location.origin).toString();
}

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
